/* eslint-disable lingui/no-unlocalized-strings */
import {
  isPRPreview,
  KnownDomains,
  TENANT_ID_TO_NAME,
} from '../utils/constants';

/**
 * Core config types and hostname-derivation primitives.
 *
 * This module is a permanent lower layer shared by the legacy URL-based config
 * (`./legacy`) and the connect-app config (`./connect`). It must not import from
 * `./legacy`, so it can outlive the legacy system once that is removed.
 */

export interface Config {
  alchemyDataEndpoint: string;
  apiEndpoint: string;
  minionApiRestEndpoint: string;
  dataDictionaryEndpoint: string;
  uploadsApiEndpoint: string;
  launchDarklyKey: string;
  keycloakUri: string;
  keycloakRealm: string;
  keycloakClientId: string;
  restApiEndpoint: string;
  subscriptionApiEndpoint: string;
  emailTenderingEndpoint: string;
  aiEndpoint: string;
  caenLockingApiEndpoint: string;
  regionsDataEndpoint: string;
  ltlPricingMatrix: string;
  flatFileLicenseKey: string;
  flatFileApiEndPoint: string;
  masterbidEndpoint: string;
  flatFilePlatformPublishableKey: string;
  flatFilePlatformEnvironmentId: string;
  /** ie chs9l-test, ml100-prod */
  envKey: string;
  documentApiEndpoint: string;
}

export const crossAppKeys = [
  'apiEndpoint',
  'keycloakUri',
  'keycloakRealm',
  'keycloakClientId',
  'launchDarklyKey',
  'envKey',
] as const;
export type CrossAppConfig = Pick<Config, (typeof crossAppKeys)[number]>;

export const configAliases: Partial<Record<KnownDomains, KnownDomains[]>> = {
  'ml100.mastermindtms.com': ['molo.mastermindtms.com', 'mm.shipmolo.com'],
  'test.ml100.mastermindtms.com': [
    'sandbox.shipmolo.com',
    'upsidedown.shipmolo.com',
  ],
  'dev.mm100.mastermindtms.com': [
    'mastery-frontend.dev.mm100.mastermindtms.com',
  ],
};

export const getTenantCodeFromHostname = (
  hostname: string,
): string | undefined => {
  if (hostname.match(/connect-carrier/)) {
    return 'connect-carrier';
  }
  if (hostname.match(/connect-customer/)) {
    return 'connect-customer';
  }
  const found = Object.entries(TENANT_ID_TO_NAME).find(([, name]) =>
    new RegExp(name).test(hostname),
  )?.[0];
  if (found) {
    return found;
  }
  let base = typeof hostname === 'string' ? hostname : '';
  const foundFromResolutions = Object.entries(configAliases).find(
    ([, testArr]) => testArr.includes(base as KnownDomains),
  )?.[0];
  if (foundFromResolutions) {
    base = foundFromResolutions;
  }
  return base
    .replace('.mastermindtms.com', '')
    .replace('dev.', '')
    .replace('test.', '');
};

export const getEnvKeyFromHostname = (hostname: string): string => {
  if (hostname === 'localhost') {
    return hostname;
  }
  if (isPRPreview(hostname)) {
    return 'pull';
  }

  // Fix envKey for molo aliases on test.ml100.mastermindtms.com (was falling back on ml100-prod
  // for sandbox.shipmolo.com and upsidedown.shipmolo.com).
  let resolvedHostname = hostname;
  const foundFromResolutions = Object.entries(configAliases).find(
    ([, testArr]) => testArr.includes(resolvedHostname as KnownDomains),
  )?.[0];
  if (foundFromResolutions) {
    resolvedHostname = foundFromResolutions;
  }

  const tenantCode = getTenantCodeFromHostname(hostname);
  let res = '';
  if (resolvedHostname.match(/dev\./)) {
    res = `${tenantCode}-dev`;
  } else if (resolvedHostname.match(/test\./)) {
    res = `${tenantCode}-test`;
  } else {
    res = `${tenantCode}-prod`;
  }
  if (tenantCode === 'connect-carrier') {
    return res.replace('connect-carrier-', '');
  }
  if (tenantCode === 'connect-customer') {
    return res.replace('connect-customer-', '');
  }
  return res;
};
